import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, MapPin, CalendarDays, Star, ChevronRight, ChevronLeft, Check, Gift, Smartphone, PartyPopper, Sparkles, BookOpen, Smile } from 'lucide-react';
import { useCommunityLang } from '../../context/CommunityLangContext';
import CommunityLangToggle from './CommunityLangToggle';

const T = '#4DB6AC';
const BG = '#F0F4F0';

const slide = {
  enter: (d) => ({ x: d > 0 ? 320 : -320, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (d) => ({ x: d > 0 ? -320 : 320, opacity: 0 }),
};

export default function CommunityOnboarding({ onComplete }) {
  const { t } = useCommunityLang();
  const [card, setCard] = useState(0);
  const [dir, setDir] = useState(1);
  const [showCelebration, setShowCelebration] = useState(false);

  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [pincode, setPincode] = useState('');
  const [interests, setInterests] = useState([]);
  const [income, setIncome] = useState('');

  const go = (to) => { setDir(to > card ? 1 : -1); setCard(to); };

  const canNext = [
    ageConfirmed,
    /^\d{6}$/.test(pincode.trim()),
    true,
    true,
  ];

  const handleFinish = () => {
    setShowCelebration(true);
    setTimeout(() => {
      onComplete({
        pincode: pincode.trim(),
        interests,
        income: income || null,
        ageConfirmed: true,
      });
    }, 2500);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: BG, zIndex: 9999, display: 'flex', flexDirection: 'column' }}>
      <CommunityLangToggle style={{ position: 'absolute', top: 14, right: 14, zIndex: 10001 }} />
      {/* Card area */}
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        <AnimatePresence initial={false} custom={dir} mode="wait">
          <motion.div
            key={card}
            custom={dir}
            variants={slide}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.28, ease: 'easeInOut' }}
            style={{ position: 'absolute', inset: 0, overflowY: 'auto', padding: '32px 20px 120px' }}
          >
            <div style={{ maxWidth: 440, margin: '0 auto' }}>
              {card === 0 && <Card1 t={t} ageConfirmed={ageConfirmed} setAgeConfirmed={setAgeConfirmed} />}
              {card === 1 && <Card2 t={t} pincode={pincode} setPincode={setPincode} />}
              {card === 2 && <Card3 t={t} interests={interests} setInterests={setInterests} income={income} setIncome={setIncome} />}
              {card === 3 && <Card4 t={t} />}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Fixed footer */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 20px 28px', background: BG,
        borderTop: '1px solid rgba(0,0,0,0.04)',
      }}>
        {/* Back */}
        <div style={{ width: 72 }}>
          {card > 0 && (
            <button data-testid="onb-back" onClick={() => go(card - 1)}
              style={{ background: 'none', border: 'none', color: T, fontSize: 15, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2 }}>
              <ChevronLeft size={18} /> {t('tour.back')}
            </button>
          )}
        </div>

        {/* Dots */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {[0,1,2,3].map(i => (
            <div key={i} style={{
              height: 8,
              width: i === card ? 24 : 8,
              borderRadius: 4,
              background: i === card ? T : '#DDD',
              transition: 'all 0.3s',
            }} />
          ))}
        </div>

        {/* Next / Let's go */}
        <div style={{ width: 120, display: 'flex', justifyContent: 'flex-end' }}>
          {card < 3 ? (
            <button data-testid="onb-next" onClick={() => canNext[card] && go(card + 1)}
              disabled={!canNext[card]}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '10px 22px', borderRadius: 50, border: 'none',
                background: canNext[card] ? T : '#D0D0D0',
                color: '#fff', fontSize: 15, fontWeight: 600,
                cursor: canNext[card] ? 'pointer' : 'not-allowed',
                boxShadow: canNext[card] ? '0 4px 12px rgba(77,182,172,0.25)' : 'none',
              }}>
              {t('tour.next')} <ChevronRight size={16} />
            </button>
          ) : (
            <button data-testid="onb-finish" onClick={handleFinish}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '10px 22px', borderRadius: 50, border: 'none',
                background: T, color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(77,182,172,0.25)',
              }}>
              {t('tour.lets_go')} <ChevronRight size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Star Celebration Overlay */}
      <AnimatePresence>
        {showCelebration && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{
              position: 'fixed', inset: 0, zIndex: 10001,
              background: 'rgba(0,0,0,0.75)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <motion.div
              initial={{ scale: 0 }} animate={{ scale: [0, 1.2, 1] }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <Sparkles size={72} color="#F59E0B" fill="#F59E0B" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              style={{ textAlign: 'center', marginTop: 20 }}
            >
              <div style={{ fontSize: 42, fontWeight: 800, color: '#F59E0B', marginBottom: 8 }}>+50</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 6 }}>{t('tour.stars_earned_50')}</div>
              <div style={{ fontSize: 14, color: '#D1D5DB' }}>{t('tour.welcome_subtitle')}</div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */

function Card1({ t, ageConfirmed, setAgeConfirmed }) {
  const features = [
    { Icon: Users, key: 'feature_exposure', color: '#4DB6AC' },
    { Icon: BookOpen, key: 'feature_learning', color: '#F28C28' },
    { Icon: Smile, key: 'feature_enjoy', color: '#4CAF8E' },
    { Icon: Star, key: 'feature_rewards', color: '#E76F51' },
  ];
  return (
    <>
      <IconCircle color={T} bg={`${T}18`}><Users size={28} color={T} /></IconCircle>
      <h1 style={h1Style}>{t('tour.welcome_title')}</h1>
      <p style={bodyStyle}>{t('tour.welcome_body')}</p>

      {/* Feature tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 24 }}>
        {features.map((f, i) => (
          <div key={i} style={{
            background: '#fff', borderRadius: 14, padding: '16px 14px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}>
            <f.Icon size={22} color={f.color} style={{ marginBottom: 10 }} />
            <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>{t(`tour.${f.key}`)}</div>
          </div>
        ))}
      </div>

      {/* Age checkbox */}
      <button
        data-testid="age-checkbox"
        onClick={() => setAgeConfirmed(v => !v)}
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%',
          padding: '14px 16px', borderRadius: 12,
          border: `1.5px solid ${ageConfirmed ? T : '#DDD'}`,
          background: '#fff', cursor: 'pointer', textAlign: 'left',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}
      >
        <div style={{
          width: 22, height: 22, borderRadius: 6, flexShrink: 0, marginTop: 1,
          border: `2px solid ${ageConfirmed ? T : '#CCC'}`,
          background: ageConfirmed ? T : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.15s',
        }}>
          {ageConfirmed && <Check size={14} color="#fff" strokeWidth={3} />}
        </div>
        <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
          {t('tour.age_confirm')}
        </span>
      </button>
    </>
  );
}

function Card2({ t, pincode, setPincode }) {
  const valid = /^\d{6}$/.test(pincode.trim());
  return (
    <>
      <IconCircle color="#F28C28" bg="#FFF3E0"><MapPin size={28} color="#F28C28" /></IconCircle>
      <h1 style={h1Style}>{t('tour.events_near_you_title')}</h1>
      <p style={bodyStyle}>{t('tour.events_near_you_body')}</p>

      {/* Example action card */}
      <div style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid #E5E7EB', marginBottom: 22, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <div style={{ background: T, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', letterSpacing: 0.5 }}>{t('tour.sample_chip_sports')}</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#fff', display: 'flex', alignItems: 'center', gap: 3 }}>
            <Star size={12} fill="#fff" color="#fff" /> {t('tour.sample_chip_stars')}
          </span>
        </div>
        <div style={{ background: '#fff', padding: '14px 14px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1A1A2E', marginBottom: 4 }}>{t('tour.sample_action_title')}</div>
          <div style={{ fontSize: 12, color: '#6B7280' }}>{t('tour.sample_action_sub')}</div>
        </div>
      </div>

      {/* Pincode input */}
      <div style={{ marginBottom: 8 }}>
        <label style={labelStyle}>{t('tour.label_pincode')}</label>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 16px', borderRadius: 10,
          border: `1px solid ${valid ? T : '#DDD'}`,
          background: '#fff', transition: 'border 0.2s',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}>
          <MapPin size={18} color={valid ? T : '#999'} />
          <input
            data-testid="onb-pincode"
            type="text"
            inputMode="numeric"
            maxLength={6}
            placeholder={t('tour.placeholder_pincode')}
            value={pincode}
            onChange={e => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: 15, color: '#1A1A2E', background: 'transparent' }}
          />
          {valid && <Check size={16} color={T} strokeWidth={3} />}
        </div>
      </div>

      <InfoBox text={t('tour.pincode_privacy')} />
    </>
  );
}

function Card3({ t, interests, setInterests, income, setIncome }) {
  const toggle = (id) => setInterests(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  // Identifiers are stable; only the rendered label is localized so the
  // backend payload doesn't change based on UI language.
  const INTEREST_ITEMS = [
    { id: 'Fun & Sport', labelKey: 'interest_fun' },
    { id: 'Learning and growth', labelKey: 'interest_learning' },
    { id: 'Creativity', labelKey: 'interest_creativity' },
    { id: 'Entertainment', labelKey: 'interest_entertainment' },
  ];
  return (
    <>
      <IconCircle color="#D84315" bg="#FAECE7"><CalendarDays size={28} color="#D84315" /></IconCircle>
      <h1 style={h1Style}>{t('tour.join_events_title')}</h1>
      <p style={bodyStyle}>{t('tour.join_events_body')}</p>

      {/* Interests chips */}
      <label style={labelStyle}>{t('tour.label_interests')}</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 22 }}>
        {INTEREST_ITEMS.map(it => {
          const sel = interests.includes(it.id);
          return (
            <button key={it.id} data-testid={`chip-${it.id}`} onClick={() => toggle(it.id)}
              style={{
                padding: '8px 16px', borderRadius: 50,
                border: `1px solid ${sel ? T : '#DDD'}`,
                background: sel ? T : '#fff', color: sel ? '#fff' : '#374151',
                fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
              }}>{t(`tour.${it.labelKey}`)}</button>
          );
        })}
      </div>

      {/* Income toggle */}
      <label style={labelStyle}>{t('tour.label_income')}</label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
        {[
          { id: 'below_5L', line1Key: 'income_low_line1', line2Key: 'income_low_line2' },
          { id: 'above_5L', line1Key: 'income_high_line1', line2Key: 'income_high_line2' },
        ].map(opt => {
          const sel = income === opt.id;
          return (
            <button key={opt.id} data-testid={`income-${opt.id}`} onClick={() => setIncome(sel ? '' : opt.id)}
              style={{
                padding: '14px 10px', borderRadius: 12, textAlign: 'center',
                border: `1.5px solid ${sel ? T : '#DDD'}`,
                background: sel ? T : '#fff', color: sel ? '#fff' : '#374151',
                cursor: 'pointer', transition: 'all 0.15s',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}>
              <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.3 }}>{t(`tour.${opt.line1Key}`)}</div>
              <div style={{ fontSize: 12, fontWeight: 400, opacity: 0.8, marginTop: 2 }}>{t(`tour.${opt.line2Key}`)}</div>
            </button>
          );
        })}
      </div>

      <InfoBox text={t('tour.income_privacy')} />
    </>
  );
}

function Card4({ t }) {
  const rewards = [
    { icon: <Gift size={22} color="#F9A825" />, iconBg: '#FFF8E1', titleKey: 'reward_vouchers', subKey: 'reward_vouchers_sub', badgeKey: 'badge_redeem', badgeBg: '#E1F5EE', badgeColor: '#0F6E56' },
    { icon: <Smartphone size={22} color="#0F6E56" />, iconBg: '#E1F5EE', titleKey: 'reward_mobile', subKey: 'reward_mobile_sub', badgeKey: 'badge_redeem', badgeBg: '#E1F5EE', badgeColor: '#0F6E56' },
    { icon: <PartyPopper size={22} color="#D84315" />, iconBg: '#FAECE7', titleKey: 'reward_more', subKey: 'reward_more_sub', badgeKey: 'badge_soon', badgeBg: '#F5F5F5', badgeColor: '#888' },
  ];
  return (
    <>
      <IconCircle color="#558B2F" bg="#EAF3DE"><Star size={28} color="#558B2F" /></IconCircle>
      <h1 style={h1Style}>{t('tour.earn_stars_title')}</h1>
      <p style={bodyStyle}>{t('tour.earn_stars_body')}</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
        {rewards.map((r, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '14px 14px',
            background: '#fff', borderRadius: 14, border: '1px solid #E5E7EB',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: r.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {r.icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1A1A2E' }}>{t(`tour.${r.titleKey}`)}</div>
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{t(`tour.${r.subKey}`)}</div>
            </div>
            <span style={{ padding: '4px 12px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: r.badgeBg, color: r.badgeColor, whiteSpace: 'nowrap' }}>{t(`tour.${r.badgeKey}`)}</span>
          </div>
        ))}
      </div>

      <div style={{ background: '#E1F5EE', borderRadius: 12, padding: '14px 16px', fontSize: 13, color: '#0F6E56', lineHeight: 1.5, fontWeight: 500 }}>
        {t('tour.all_set')}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════ */

function IconCircle({ children, color, bg }) {
  return (
    <div style={{ width: 56, height: 56, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
      {children}
    </div>
  );
}

function InfoBox({ text }) {
  return (
    <div style={{ background: '#E1F5EE', borderRadius: 10, padding: '12px 14px', fontSize: 12, color: '#0F6E56', lineHeight: 1.5, marginTop: 4 }}>
      {text}
    </div>
  );
}

const h1Style = { fontSize: 24, fontWeight: 700, color: '#1A1A2E', margin: '0 0 10px', lineHeight: 1.3 };
const bodyStyle = { fontSize: 14, color: '#555', lineHeight: 1.65, margin: '0 0 24px' };
const labelStyle = { display: 'block', fontSize: 11, fontWeight: 700, color: '#999', letterSpacing: 1, marginBottom: 10, textTransform: 'uppercase' };
